import { useState, forwardRef, useImperativeHandle } from 'react';
import { InstrumentPill, ValuePill } from './IndicatorPreviewFields';

export type IndicatorEditorAdditionalInstrumentRef = {
  getConfig: () => {
    symbol: string;
    adjusted: boolean;
  };
};

type Props = {
  editMode: boolean;
};

const IndicatorEditorAdditionalInstrument = forwardRef<IndicatorEditorAdditionalInstrumentRef, Props>(({ editMode }, ref) => {
  const [symbol, setSymbol] = useState('');
  const [adjusted, setAdjusted] = useState(false);

  useImperativeHandle(ref, () => ({
    getConfig: () => ({ symbol, adjusted }),
  }));

  return (
    <div>
      {editMode ? (
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label htmlFor="additional-instrument-symbol" className="text-sm font-medium text-ink">
              Symbol:
            </label>
            <input
              id="additional-instrument-symbol"
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              placeholder="e.g. SPY or ^VIX"
              className="border-2 border-rule rounded-sm bg-newsprint-panel text-ink px-3 py-2 min-w-[8rem] focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-ink">Prices:</span>
            <div
              role="radiogroup"
              aria-label="Adjusted or unadjusted prices"
              className="inline-flex rounded-sm border border-rule bg-newsprint-dark p-0.5"
            >
              {(['adjusted', 'unadjusted'] as const).map((mode) => {
                const isAdjusted = mode === 'adjusted';
                return (
                  <button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={adjusted === isAdjusted}
                    onClick={() => setAdjusted(isAdjusted)}
                    className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                      adjusted === isAdjusted
                        ? 'bg-ink text-newsprint'
                        : 'text-ink hover:bg-newsprint'
                    }`}
                  >
                    {mode === 'adjusted' ? 'Adjusted' : 'Unadjusted'}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <InstrumentPill>{symbol || '—'}</InstrumentPill>
          <ValuePill label="Adjusted" value={adjusted ? 'Yes' : 'No'} />
        </div>
      )}
    </div>
  );
});

IndicatorEditorAdditionalInstrument.displayName = 'IndicatorEditorAdditionalInstrument';

export default IndicatorEditorAdditionalInstrument;
